import { z } from "zod";
import { FORM_SLUG_MAX } from "./formNames";
import { FORM_INPUT_TYPES, FORM_LIMITS, FORM_MAPPINGS, formInputsOf, type FormField, type FormFields, type FormMapping, type FormTags } from "./forms";
import { FORM_PREFILL_SOURCES, type FormPrefillSource, type FormProjectRef, type FormValues } from "./formSubmissions";

/** The form builder, round three (brief docs/briefs/forms-round-three.md with "After the red team's review"; red team report findings 6, 9,
 *  10, 15 and 19): the pure rules a submission's effects follow, so the migration, the e2e seed, the submission route, the ticket page and
 *  the AI read one truth.
 *
 *  In plain words: the project's **old form** (a switch, a product list, a licence question) becomes a form like any other; a submission's
 *  **tags** are the form's fixed lists plus the rules whose select matched; a **licence** a person types is never kept or shown in full; the
 *  **submission block** is the form's fields as the team reads them at the top of the ticket, each marked verified only when the company's
 *  own site vouched for it. */

/** the old form's settings as the migration reads them */
export type LegacyFormSettings = { products: readonly string[]; askLicense: boolean };

/** The fields a project's old web form becomes, in the old page's order: name, email, product (a plain optional select, only when the
 *  project offered products: each product once, at most 50), subject, message, licence (only when the old form asked for one). Migration
 *  0109's SQL builds exactly this (`migration0109Data.test.ts` compares the two). */
export function legacyFormFields(o: LegacyFormSettings): FormFields {
  const products = [...new Set(o.products)].slice(0, FORM_LIMITS.options);
  const items: FormField[] = [
    { key: "name", type: "text", label: "Your name", required: true, mapping: "name" },
    { key: "email", type: "email", label: "Email", required: true, mapping: "email" },
    ...(products.length ? [{ key: "product", type: "select", label: "Product", required: false, options: products.map((p) => ({ value: p, label: p })) } satisfies FormField] : []),
    { key: "subject", type: "text", label: "Subject", required: true, placeholder: "One line, like an email subject", mapping: "subject" },
    { key: "message", type: "textarea", label: "Message", required: true, placeholder: "What happened, what you expected, and what you already tried", mapping: "message" },
    ...(o.askLicense ? [{ key: "licence", type: "text", label: "Licence key", required: false, placeholder: "If you have one at hand", mapping: "licence" } satisfies FormField] : []),
  ];
  return { v: 1, items };
}

/** The migrated form's slug: the project's when no form of the workspace has it, else `<slug>-form`, then `<slug>-form-2`, `-3` …, the
 *  project's part cut so the whole stays within 40 characters (never a hyphen left at the cut). */
export function migratedFormSlug(projectSlug: string, taken: readonly string[]): string {
  const used = new Set(taken);
  if (!used.has(projectSlug)) return projectSlug;
  for (let n = 1; ; n++) {
    const suffix = n === 1 ? "-form" : `-form-${n}`;
    const slug = `${projectSlug.slice(0, FORM_SLUG_MAX - suffix.length).replace(/-+$/, "")}${suffix}`;
    if (!used.has(slug)) return slug;
  }
}

/** Which tag ids a submission applies: the form's fixed lists first, then every rule whose field's submitted value equals its option exactly
 *  (case and spaces count), in the rules' order, each id once. Whether an id still exists, is of the right kind and fits the ticket's
 *  project is the server's check. */
export function submissionTags(tags: FormTags, values: Readonly<Record<string, string | boolean | undefined>>): { ticket: string[]; customer: string[] } {
  const ticket = new Set(tags.ticket); const customer = new Set(tags.customer);
  for (const r of tags.rules) {
    if (values[r.field] !== r.equals) continue;
    r.ticket.forEach((id) => ticket.add(id)); r.customer.forEach((id) => customer.add(id));
  }
  return { ticket: [...ticket], customer: [...customer] };
}

/** A licence or API key as the team, the database and the AI may see it: the first and last four characters (the old site form's rule) */
export const maskLicenceKey = (key: string): string => (key.length >= 8 ? `${key.slice(0, 4)}-··········-${key.slice(-4)}` : "··········");

/** the checked values with every licence-mapped value masked (finding 6: what is stored, written into the message and shown) */
export function maskFormValues(fields: FormFields, values: FormValues): FormValues {
  const licence = formInputsOf(fields.items).find((f) => f.mapping === "licence")?.key;
  const v = licence ? values[licence] : undefined;
  return typeof v === "string" && v ? { ...values, [licence!]: maskLicenceKey(v) } : values;
}

/** How a submission's project was chosen (finding 20): the person's pick in the form, the snippet's or the plugin's placement, the form's
 *  default */
export const FORM_PLACED_BY = ["field", "placement", "default"] as const;
export type FormPlacedBy = (typeof FORM_PLACED_BY)[number];

/** one field of the submission block: the value as stored (a licence masked), the value as the team reads it (an option's label, a
 *  project's name, Yes or No), its mapping, whether the company's own site vouched for it, and who filled it in when the person did not type
 *  it (round two's prefill marks: the page's link, the container's attributes, the shop's call, the plugin; null: typed) */
export const TicketSubmissionFieldSchema = z.object({ key: z.string(), label: z.string(), type: z.enum(FORM_INPUT_TYPES), value: z.string(), shown: z.string(), mapping: z.enum(FORM_MAPPINGS).nullable(), verified: z.boolean(), filledBy: z.enum(FORM_PREFILL_SOURCES).nullable() });
export type TicketSubmissionField = z.infer<typeof TicketSubmissionFieldSchema>;
/** the ticket read's `submission`: the form, the project and how it was chosen (null for a row older than round three), the fields in the
 *  form's order (every field but files, empty ones included as ""; never a Text block) */
export const TicketSubmissionSchema = z.object({ formId: z.string().nullable(), formName: z.string(), project: z.object({ id: z.string(), chosenBy: z.enum(FORM_PLACED_BY).nullable() }), fields: z.array(TicketSubmissionFieldSchema) });
export type TicketSubmission = z.infer<typeof TicketSubmissionSchema>;

const asText = (v: string | boolean | undefined): string => (typeof v === "boolean" ? (v ? "Yes" : "No") : v ?? "");
function shownOf(f: FormField, value: string, projects: readonly FormProjectRef[]): string {
  if (!value || f.type === "checkbox") return value;
  if (f.mapping === "project") return projects.find((p) => p.id === value)?.name ?? f.options?.find((o) => o.value === value)?.label ?? value;
  if (f.type === "select") return f.options?.find((o) => o.value === value)?.label ?? value;
  return value;
}

/** The submission block from a stored submission and its form: a value counts as verified only when its mark is "plugin" on a submission
 *  sent with a site's key (finding 3: a browser's claim never counts). */
export function ticketSubmissionOf(o: { formId: string | null; formName: string; fields: FormFields; answers: FormValues; marks: Record<string, FormPrefillSource>; siteId: string | null; projectId: string; placedBy: FormPlacedBy | null; projects: readonly FormProjectRef[] }): TicketSubmission {
  const fields = formInputsOf(o.fields.items).filter((f) => f.type !== "file").map((f): TicketSubmissionField => {
    const value = asText(o.answers[f.key]);
    const filledBy = value ? o.marks[f.key] ?? null : null;
    return { key: f.key, label: f.label, type: f.type, value, shown: shownOf(f, value, o.projects), mapping: f.mapping ?? null, verified: !!o.siteId && filledBy === "plugin", filledBy };
  });
  return { formId: o.formId, formName: o.formName, project: { id: o.projectId, chosenBy: o.placedBy }, fields };
}

/** What the AI reads of a submission (question 4): the filled fields as "label, value, verified", in the form's own fenced block, never as
 *  the company's word */
export type FormEntry = { form: string; fields: { label: string; value: string; verified: boolean }[] };
export const formEntryOf = (s: TicketSubmission): FormEntry => ({ form: s.formName, fields: s.fields.filter((f) => f.value).map((f) => ({ label: f.label, value: f.shown, verified: f.verified })) });

/** the mappings the customer card's "From the form" block shows, in this order */
export const FORM_CARD_MAPPINGS = ["name", "company", "licence", "order"] as const satisfies readonly FormMapping[];
